/** ZIP32 + conditional single-PUT budget. Larger formats/transports need their own policy. */
export const FOLDER_ARCHIVE_MAX_BYTES = 2 * 1024 ** 3;
export const DOWNLOAD_DISK_RESERVE_BYTES = 1024 ** 3;
export const ARCHIVE_LIMIT_ERROR = 'DOWNLOAD_ARCHIVE_LIMIT_EXCEEDED';
export const ARCHIVE_DISK_ERROR = 'DOWNLOAD_ARCHIVE_DISK_UNAVAILABLE';

export type ArchiveSizeEstimate = {
  knownSourceBytes: number;
  unknownSizeItemCount: number;
  largestSourceBytes: number;
  archiveOverheadBytes: number;
};

export function estimateFolderArchive(
  items: readonly { relativePath: string; sizeBytes?: number }[]
): ArchiveSizeEstimate {
  let knownSourceBytes = 0;
  let unknownSizeItemCount = 0;
  let largestSourceBytes = 0;
  let archiveOverheadBytes = 22;
  const encoder = new TextEncoder();
  for (const item of items) {
    // Reserve room for duplicate-name suffixes as well as local/central headers.
    archiveOverheadBytes +=
      76 + 2 * (encoder.encode(item.relativePath).length + 32);
    if (!Number.isSafeInteger(item.sizeBytes) || item.sizeBytes! < 0) {
      unknownSizeItemCount++;
      continue;
    }
    knownSourceBytes += item.sizeBytes!;
    largestSourceBytes = Math.max(largestSourceBytes, item.sizeBytes!);
    if (!Number.isSafeInteger(knownSourceBytes))
      throw new Error(ARCHIVE_LIMIT_ERROR);
  }
  return {
    knownSourceBytes,
    unknownSizeItemCount,
    largestSourceBytes,
    archiveOverheadBytes,
  };
}

export function assertArchiveFits(
  estimate: ArchiveSizeEstimate,
  maxArchiveBytes: number
): void {
  if (
    estimate.knownSourceBytes + estimate.archiveOverheadBytes >
    maxArchiveBytes
  ) {
    throw new Error(ARCHIVE_LIMIT_ERROR);
  }
}

export function getFolderArchiveBudget(
  estimate: ArchiveSizeEstimate,
  freeDiskBytes: number
): number {
  assertArchiveFits(estimate, FOLDER_ARCHIVE_MAX_BYTES);
  if (
    !Number.isFinite(freeDiskBytes) ||
    freeDiskBytes <= DOWNLOAD_DISK_RESERVE_BYTES
  ) {
    throw new Error(ARCHIVE_DISK_ERROR);
  }
  // Each source is spooled before copying into the archive. Reserve twice the
  // archive budget so stale/missing size metadata cannot exhaust temporary disk.
  const budget = Math.min(
    FOLDER_ARCHIVE_MAX_BYTES,
    Math.floor((freeDiskBytes - DOWNLOAD_DISK_RESERVE_BYTES) / 2)
  );
  if (estimate.knownSourceBytes + estimate.archiveOverheadBytes > budget) {
    throw new Error(ARCHIVE_DISK_ERROR);
  }
  return budget;
}

export function isPermanentArchiveSizeError(message?: string): boolean {
  // The old 500 MiB error remains retryable after the budget upgrade.
  return message === ARCHIVE_LIMIT_ERROR;
}

/** Treat exhausted spool storage as an archive failure, never a skipped file. */
export function isArchiveDiskError(error: unknown): boolean {
  let current = error;
  for (let depth = 0; depth < 4 && current instanceof Error; depth++) {
    if (
      'code' in current &&
      (current.code === 'ENOSPC' || current.code === 'EDQUOT')
    )
      return true;
    if (current.message === ARCHIVE_DISK_ERROR) return true;
    current = current.cause;
  }
  return false;
}
